import { useState, useEffect } from "react";
import { fmtMoneyExact, Money } from "@/lib/money";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, Btn, th as palette } from "@/lib/bricks";
import { Page, PageHeader, Stats, StatusTabs, Section, List, Row, Meta, Empty, Loading, humanize, figLabel } from "@/components/calm/Calm";
import { fmtDay } from "@/lib/dates";

const fmtCAD = fmtMoneyExact;

export default function ReportsPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const [activeTab, setActiveTab] = useState('Overview');
  const [overview, setOverview] = useState(null);
  const [agentStats, setAgentStats] = useState([]);
  const [txStats, setTxStats] = useState([]);
  const [financial, setFinancial] = useState([]);
  const [loading, setLoading] = useState(true);

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/reports/overview`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${BASE}/api/reports/agents`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/reports/transactions`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/reports/financial`, { headers }).then(r => r.json()).catch(() => []),
    ]).then(([ov, ag, tx, fi]) => {
      setOverview(ov && typeof ov === 'object' && !Array.isArray(ov) && ov.detail == null ? ov : { active_transactions: 0, total_volume_ytd: 0, commission_ytd: 0, avg_days_to_close: null, recent_activity: [] });
      setAgentStats(Array.isArray(ag) ? ag : []);
      setTxStats(Array.isArray(tx) ? tx : []);
      setFinancial(Array.isArray(fi) ? fi : []);
      setLoading(false);
    });
  }, []); // eslint-disable-line

  const TABS = ['Overview', 'Agents', 'Transactions', 'Financial'];

  if (loading) return <Loading />;

  const exportCsv = () => {
    const rows = activeTab === 'Agents' ? agentStats
      : activeTab === 'Transactions' ? txStats
      : activeTab === 'Financial' ? financial
      : (overview?.recent_activity || []);
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(','), ...rows.map(r => keys.map(k => JSON.stringify(r[k] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `office-reports-${activeTab.toLowerCase()}.csv`;
    a.click();
  };

  const fig = (text) => ({ text, figure: true });
  const statusTotals = ['active','pending','completed','cancelled','fallen_through'].map(status => ({
    status, count: txStats.reduce((s, r) => s + (r[status] || 0), 0),
  }));

  return (
    <Page testid="reports-page">
      <PageHeader title="Reports" subtitle="How the office is performing"
        action={<Btn dark={dark} data-testid="reports-export" onClick={exportCsv}>Export CSV</Btn>} />

      <StatusTabs hit testid="reports-tabs" label="Report" value={activeTab} onChange={setActiveTab}
        options={TABS.map(t => ({ key: t, label: t }))} />

      {activeTab === 'Overview' && (
        <>
          <Stats items={[
            { label: 'Active transactions', value: overview?.active_transactions ?? 0 },
            { label: 'Volume closed, YTD', value: <Money value={overview?.total_volume_ytd} /> },
            { label: 'Office commission, YTD', value: <Money value={overview?.commission_ytd} /> },
          ]} />
          {overview?.avg_days_to_close ? (
            <div style={{ fontSize: 12.5, color: p.ink3, marginTop: -12 }}>Deals close in <span style={{ fontFamily: F.mono }}>{overview.avg_days_to_close}</span> days on average.</div>
          ) : null}
          <Section title="Recent activity">
            {!(overview?.recent_activity || []).length ? <Empty>No recent activity.</Empty> : (
              <List testid="reports-activity">
                {overview.recent_activity.slice(0, 10).map((act, i) => (
                  <Row key={i} title={act.description || humanize(act.type)} meta={[act.user_name].filter(Boolean)}
                    next={act.created_at ? [fig(fmtDay(act.created_at))] : null}
                    status={humanize(act.type)} />
                ))}
              </List>
            )}
          </Section>
        </>
      )}

      {activeTab === 'Agents' && (
        agentStats.length === 0 ? <Empty>No agent numbers yet. They appear once deals close.</Empty> : (
          <List testid="reports-agents">
            {agentStats.map((a, i) => (
              <Row key={a.agent_id || i} title={a.name}
                meta={[`${a.deals_closed ?? 0} closed this year`, `${a.deals_active ?? 0} active`, `${a.jobs_completed ?? 0} shoots`]}
                next={[figLabel(<Money value={a.gross_volume} />, 'volume'), figLabel(fmtCAD(a.commission_earned), 'commission')]} />
            ))}
          </List>
        )
      )}

      {activeTab === 'Transactions' && (
        txStats.length === 0 ? <Empty>No transaction numbers yet.</Empty> : (
          <>
            <Section title="By status">
              <Meta parts={statusTotals.map(x => `${x.count} ${humanize(x.status).toLowerCase()}`)} style={{ fontSize: 13.5, color: p.ink2 }} />
            </Section>
            <Section title="By month">
              <List testid="reports-months">
                {txStats.map((row, i) => (
                  <Row key={i} title={row.month}
                    meta={[`${row.total ?? 0} deals`, `${row.purchase ?? 0} purchase`, `${row.sale ?? 0} sale`, `${row.lease ?? 0} lease`]}
                    next={[figLabel(<Money value={row.volume} />, 'volume')]} />
                ))}
              </List>
            </Section>
          </>
        )
      )}

      {activeTab === 'Financial' && (
        financial.length === 0 ? <Empty>No income recorded yet.</Empty> : (
          <Section title="Monthly income">
            <List testid="reports-financial">
              {financial.map((row, i) => (
                <Row key={i} title={row.month}
                  meta={[figLabel(fmtCAD(row.gross_commission), 'gross'), figLabel(fmtCAD(row.brokerage_income), 'office'), figLabel(fmtCAD(row.agent_payouts), 'to agents')]}
                  next={[figLabel(fmtCAD((row.brokerage_income || 0) - (row.agent_payouts || 0)), 'net')]} />
              ))}
            </List>
          </Section>
        )
      )}
    </Page>
  );
}
